/**
 * @description
 * Utility functions for time-series data aggregation, trend analysis, and anomaly detection.
 * Handles temporal analysis of domain activity patterns.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/utils/timeSeriesUtils.test.ts
 */

import type {
  StreamRecord,
  TimeSeriesDataPoint,
  TimeSeriesAggregation,
  DomainTrend,
  DomainSnapshot,
  DomainAggregation,
  DomainStatistics,
  VSCodeLensTable,
  SnapshotComparison,
  Anomaly,
} from '../../types/vscode-lens';
import { extractDomain } from './domainUtils';

/**
 * Aggregate stream data by time periods
 * Groups records by time buckets and counts activity per domain
 * 
 * @param data - Array of stream records
 * @param granularity - Time bucket size ('hour' or 'day')
 * @param emailColumn - Column containing email addresses
 * @param startTime - Optional start time filter (Unix timestamp)
 * @param endTime - Optional end time filter (Unix timestamp)
 * @returns Time-series aggregation with data points and trends
 * 
 * @example
 * const aggregation = aggregateByTime(records, 'hour', 'user_email');
 * console.log(aggregation.dataPoints); // Hourly activity data
 */
export function aggregateByTime(
  data: StreamRecord[],
  granularity: 'hour' | 'day',
  emailColumn: string,
  startTime?: number,
  endTime?: number
): TimeSeriesAggregation {
  if (data.length === 0) {
    return {
      granularity,
      startTime: startTime || 0,
      endTime: endTime || 0,
      dataPoints: [],
      topDomains: [],
      totalRecords: 0,
      trends: [],
    };
  }

  // Determine time bucket size in milliseconds
  const bucketSize = granularity === 'hour' ? 3600000 : 86400000;
  
  // Map to store aggregated data: "timestamp-domain" -> data point
  const dataPointMap = new Map<string, TimeSeriesDataPoint>();
  
  // Map to track unique users per domain per time bucket
  const uniqueUsersMap = new Map<string, Set<string>>();
  
  // Determine actual time range from data
  let minTime = Infinity;
  let maxTime = -Infinity;
  
  // Process each record
  data.forEach(record => {
    // Extract timestamp (try common timestamp field names)
    const timestamp = getTimestamp(record);
    if (!timestamp) return;
    
    // Apply time filters if provided
    if (startTime && timestamp < startTime) return;
    if (endTime && timestamp > endTime) return;
    
    // Track time range
    minTime = Math.min(minTime, timestamp);
    maxTime = Math.max(maxTime, timestamp);
    
    // Round timestamp to bucket
    const bucketTimestamp = Math.floor(timestamp / bucketSize) * bucketSize;
    
    // Extract domain from email
    const email = record[emailColumn] as string;
    const domain = extractDomain(email);
    if (!domain) return;
    
    // Create unique key for this time bucket and domain
    const key = `${bucketTimestamp}-${domain}`;
    
    // Get or create data point
    let dataPoint = dataPointMap.get(key);
    if (!dataPoint) {
      dataPoint = {
        timestamp: bucketTimestamp,
        domain,
        count: 0,
        uniqueUsers: 0,
        activityType: getTableName(record),
      };
      dataPointMap.set(key, dataPoint);
    }
    
    // Increment count
    dataPoint.count++;
    
    // Track unique users
    const userKey = `${bucketTimestamp}-${domain}`;
    let users = uniqueUsersMap.get(userKey);
    if (!users) {
      users = new Set();
      uniqueUsersMap.set(userKey, users);
    }
    
    // Add user to set (use email or user_id)
    const userId = (record.user_id as string) || email;
    if (userId) {
      users.add(userId);
    }
  });
  
  // Update unique user counts
  uniqueUsersMap.forEach((users, key) => {
    const dataPoint = dataPointMap.get(key);
    if (dataPoint) {
      dataPoint.uniqueUsers = users.size;
    }
  });
  
  // Convert map to array and sort by timestamp
  const dataPoints = Array.from(dataPointMap.values()).sort(
    (a, b) => a.timestamp - b.timestamp
  );
  
  // Calculate top domains
  const domainCounts = new Map<string, number>();
  dataPoints.forEach(point => {
    const current = domainCounts.get(point.domain) || 0;
    domainCounts.set(point.domain, current + point.count);
  });
  
  const topDomains = Array.from(domainCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([domain]) => domain);
  
  return {
    granularity,
    startTime: startTime || minTime,
    endTime: endTime || maxTime,
    dataPoints,
    topDomains,
    totalRecords: data.length,
    trends: [], // Will be calculated separately
  };
}

/**
 * Calculate trends between two time periods
 * Compares current period with previous period to identify growth/decline
 * 
 * @param current - Current period aggregation
 * @param previous - Previous period aggregation
 * @returns Array of domain trends with percentage changes
 * 
 * @example
 * const trends = calculateTrends(currentWeek, previousWeek);
 * trends.forEach(t => console.log(`${t.domain}: ${t.percentageChange}%`));
 */
export function calculateTrends(
  current: TimeSeriesAggregation,
  previous: TimeSeriesAggregation
): DomainTrend[] {
  // Aggregate counts by domain for each period
  const currentCounts = aggregateDomainCounts(current.dataPoints);
  const previousCounts = aggregateDomainCounts(previous.dataPoints);
  
  // Get all unique domains from both periods
  const allDomains = new Set([
    ...Array.from(currentCounts.keys()),
    ...Array.from(previousCounts.keys()),
  ]);
  
  const trends: DomainTrend[] = [];
  
  allDomains.forEach(domain => {
    const currentCount = currentCounts.get(domain) || 0;
    const previousCount = previousCounts.get(domain) || 0;
    
    // Calculate percentage change
    let percentageChange = 0;
    if (previousCount > 0) {
      percentageChange = ((currentCount - previousCount) / previousCount) * 100;
    } else if (currentCount > 0) {
      percentageChange = 100; // New domain
    }
    
    // Determine trend direction
    let trend: 'up' | 'down' | 'stable';
    if (Math.abs(percentageChange) < 5) {
      trend = 'stable';
    } else if (percentageChange > 0) {
      trend = 'up';
    } else {
      trend = 'down';
    }
    
    // Calculate confidence based on sample size
    const totalCount = currentCount + previousCount;
    const confidence = Math.min(totalCount / 100, 1); // Max confidence at 100+ records
    
    trends.push({
      domain,
      currentPeriod: currentCount,
      previousPeriod: previousCount,
      percentageChange,
      trend,
      confidence,
    });
  });
  
  // Sort by absolute percentage change (most significant first)
  trends.sort((a, b) => Math.abs(b.percentageChange) - Math.abs(a.percentageChange));
  
  return trends;
}

/**
 * Detect anomalies in time-series data
 * Identifies unusual spikes, drops, or patterns in domain activity
 * 
 * @param data - Array of time-series data points
 * @param threshold - Standard deviation threshold for anomaly detection (default: 3)
 * @returns Array of detected anomalies
 * 
 * @example
 * const anomalies = detectAnomalies(dataPoints, 2.5);
 * anomalies.forEach(a => console.log(`Anomaly: ${a.description}`));
 */
export function detectAnomalies(
  data: TimeSeriesDataPoint[],
  threshold: number = 3
): Anomaly[] {
  if (data.length < 10) {
    return []; // Need sufficient data for anomaly detection
  }
  
  const anomalies: Anomaly[] = [];
  
  // Group by domain
  const domainData = new Map<string, TimeSeriesDataPoint[]>();
  data.forEach(point => {
    const points = domainData.get(point.domain) || [];
    points.push(point);
    domainData.set(point.domain, points);
  });
  
  // Analyze each domain
  domainData.forEach((points, domain) => {
    if (points.length < 5) return; // Need minimum data points
    
    // Sort by timestamp
    points.sort((a, b) => a.timestamp - b.timestamp);
    
    // Calculate mean and standard deviation
    const counts = points.map(p => p.count);
    const mean = counts.reduce((sum, c) => sum + c, 0) / counts.length;
    const variance = counts.reduce((sum, c) => sum + Math.pow(c - mean, 2), 0) / counts.length;
    const stdDev = Math.sqrt(variance);
    
    // Detect anomalies
    points.forEach(point => {
      const deviation = Math.abs(point.count - mean);
      const zScore = stdDev > 0 ? deviation / stdDev : 0;
      
      if (zScore > threshold) {
        const deviationPercent = mean > 0 ? (deviation / mean) * 100 : 0;
        
        // Determine anomaly type
        let type: 'spike' | 'drop' | 'unusual_pattern';
        if (point.count > mean) {
          type = 'spike';
        } else {
          type = 'drop';
        }
        
        // Determine severity
        let severity: 'low' | 'medium' | 'high';
        if (zScore > threshold * 2) {
          severity = 'high';
        } else if (zScore > threshold * 1.5) {
          severity = 'medium';
        } else {
          severity = 'low';
        }
        
        anomalies.push({
          timestamp: point.timestamp,
          domain,
          type,
          severity,
          expected: Math.round(mean),
          actual: point.count,
          deviation: deviationPercent,
          description: `${type === 'spike' ? 'Unusual spike' : 'Unusual drop'} in activity for ${domain}: ${point.count} records (expected ~${Math.round(mean)})`,
        });
      }
    });
  });
  
  // Sort by severity and deviation
  anomalies.sort((a, b) => {
    const severityOrder = { high: 3, medium: 2, low: 1 };
    const severityDiff = severityOrder[b.severity] - severityOrder[a.severity];
    if (severityDiff !== 0) return severityDiff;
    return b.deviation - a.deviation;
  });
  
  return anomalies;
}

/**
 * Create a snapshot of current domain analytics
 * Captures current state for historical comparison
 * 
 * @param table - Table name
 * @param data - Current stream data
 * @param aggregation - Domain aggregation
 * @param statistics - Domain statistics
 * @param reason - Why snapshot was created
 * @param notes - Optional notes
 * @returns Domain snapshot object
 */
export function createSnapshot(
  table: VSCodeLensTable,
  data: StreamRecord[],
  aggregation: DomainAggregation,
  statistics: DomainStatistics,
  reason: 'scheduled' | 'manual' | 'threshold',
  notes?: string
): DomainSnapshot {
  return {
    id: generateSnapshotId(),
    timestamp: Date.now(),
    table,
    aggregation,
    statistics,
    metadata: {
      recordCount: data.length,
      domainCount: aggregation.domains.length,
      captureReason: reason,
      notes,
    },
  };
}

/**
 * Compare two snapshots to identify changes
 * Analyzes differences in domain distribution and statistics
 * 
 * @param snapshot1 - First snapshot (older)
 * @param snapshot2 - Second snapshot (newer)
 * @returns Comparison result with changes
 */
export function compareSnapshots(
  snapshot1: DomainSnapshot,
  snapshot2: DomainSnapshot
): SnapshotComparison {
  const domains1 = new Set(snapshot1.aggregation.domains.map(d => d.domain));
  const domains2 = new Set(snapshot2.aggregation.domains.map(d => d.domain));
  
  // Find added and removed domains
  const added = Array.from(domains2).filter(d => !domains1.has(d));
  const removed = Array.from(domains1).filter(d => !domains2.has(d));
  
  // Find changed domains
  const changed: Array<{
    domain: string;
    oldCount: number;
    newCount: number;
    percentageChange: number;
  }> = [];
  
  const domainMap1 = new Map(
    snapshot1.aggregation.domains.map(d => [d.domain, d.count])
  );
  const domainMap2 = new Map(
    snapshot2.aggregation.domains.map(d => [d.domain, d.count])
  );
  
  domains1.forEach(domain => {
    if (domains2.has(domain)) {
      const oldCount = domainMap1.get(domain) || 0;
      const newCount = domainMap2.get(domain) || 0;
      const percentageChange = oldCount > 0 
        ? ((newCount - oldCount) / oldCount) * 100 
        : 0;
      
      if (Math.abs(percentageChange) > 5) {
        changed.push({
          domain,
          oldCount,
          newCount,
          percentageChange,
        });
      }
    }
  });
  
  // Sort changed by absolute percentage change
  changed.sort((a, b) => Math.abs(b.percentageChange) - Math.abs(a.percentageChange));
  
  // Calculate statistics changes
  const totalRecordsChange = 
    ((snapshot2.metadata.recordCount - snapshot1.metadata.recordCount) / 
    snapshot1.metadata.recordCount) * 100;
  
  const totalDomainsChange = 
    ((snapshot2.metadata.domainCount - snapshot1.metadata.domainCount) / 
    snapshot1.metadata.domainCount) * 100;
  
  const concentrationChange = 
    snapshot2.statistics.domainConcentration - 
    snapshot1.statistics.domainConcentration;
  
  return {
    snapshot1,
    snapshot2,
    timeDifference: snapshot2.timestamp - snapshot1.timestamp,
    domainChanges: {
      added,
      removed,
      changed,
    },
    statisticsChange: {
      totalRecordsChange,
      totalDomainsChange,
      concentrationChange,
    },
  };
}

/**
 * Format time label for charts based on granularity
 *
 * @param timestamp - Unix timestamp
 * @param granularity - Time granularity
 * @returns Formatted time string
 */
export function formatTimeLabel(
  timestamp: number,
  granularity: 'hour' | 'day' | 'week' | 'month'
): string {
  const date = new Date(timestamp);
  
  switch (granularity) {
    case 'hour':
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        hour12: true,
      });
    case 'day':
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    case 'week':
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    case 'month':
      return date.toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    default:
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
      });
  }
}

/**
 * Calculate time range for a given preset
 * 
 * @param preset - Time preset ('last24h', 'last7d', etc.)
 * @returns Start and end timestamps
 */
export function getTimeRange(preset: string): { start: number; end: number } {
  const now = Date.now();
  let start: number;
  
  switch (preset) {
    case 'lastDay':
      start = now - 86400000; // 24 hours
      break;
    case 'lastWeek':
      start = now - 604800000; // 7 days
      break;
    case 'lastMonth':
      start = now - 2592000000; // 30 days
      break;
    default:
      start = now - 86400000; // Default to last day
  }
  
  return { start, end: now };
}

// Helper functions

/**
 * Extract timestamp from record
 * Tries common timestamp field names
 */
function getTimestamp(record: StreamRecord): number | null {
  // Try common timestamp fields
  const timestampFields = ['timestamp', 'created_at', 'event_time', 'time'];
  
  for (const field of timestampFields) {
    const value = record[field];
    if (value) {
      // Handle both Unix timestamps and ISO strings
      if (typeof value === 'number') {
        // If it's in seconds, convert to milliseconds
        return value > 10000000000 ? value : value * 1000;
      } else if (typeof value === 'string') {
        const parsed = Date.parse(value);
        if (!isNaN(parsed)) {
          return parsed;
        }
      }
    }
  }
  
  return null;
}

/**
 * Get table name from record
 * Infers table type from record structure
 */
function getTableName(record: StreamRecord): VSCodeLensTable {
  if ('commit_hash' in record) return 'commit_events';
  if ('message_content' in record) return 'conversation_messages';
  if ('completion_text' in record) return 'llm_completions';
  return 'commit_events'; // Default
}

/**
 * Aggregate domain counts from data points
 */
function aggregateDomainCounts(
  dataPoints: TimeSeriesDataPoint[]
): Map<string, number> {
  const counts = new Map<string, number>();
  
  dataPoints.forEach(point => {
    const current = counts.get(point.domain) || 0;
    counts.set(point.domain, current + point.count);
  });
  
  return counts;
}

/**
 * Generate unique snapshot ID
 */
function generateSnapshotId(): string {
  return `snapshot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */